/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import {useRouter} from 'next/router';
import usePageReady from '@ion/applications/src/hooks/usePageReady';
import useElementResize from '@ion/core/src/hooks/useElementResize';
import Features, {ANALYTICS_PAGE} from '../../security/Features';
import {Route} from '../RouteDefinitions';
import DashboardsPage from './DashboardsPage';

/**
 * 
 * `PreLoadDashboardPage` is a wrapper component that conditionally renders the
 * `DashboardsPage` based on user permissions and the current route.
 *
 * It is primarily used to **pre-mount** the dashboard page so that it is ready
 * when the user navigates to it, improving perceived load performance.
 *
 * ```typescript
 * import { PreLoadDashboardPage } from './PreLoadDashboardPage';
 *
 * function App() {
 *   return (
 *     <div>
 *       <PreLoadDashboardPage />
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns containing `DashboardsPage` if the user
 *          has access; otherwise, returns `undefined`.
 */
export const PreLoadDashboardPage = () => {
  const route     = useRouter();
  const {ready}   = useTranslation(['common', 'sisense']);
  const {isReady} = usePageReady({tReady: ready});
  const hasAccess = isReady && Features.hasFeature(ANALYTICS_PAGE);
  const isAllowed = isReady && hasAccess;
  const show      = route?.route === `/${Route.ANALYTICS_DASHBOARDS}`;

  const dimensions = useElementResize('layout-root');

  const style = useMemo(() => {
    if (show) {
      return ({
        display  : 'block',
        position : 'absolute',
        zIndex   : 9,
        ...dimensions
      });
    }
    return ({
      display : 'none'
    });
  }, [show, dimensions]);

  if (isAllowed) {
    return (
      <div style={style}>
        <DashboardsPage active={show} />
      </div>
    );
  }
};

export default PreLoadDashboardPage;
